/**
 * MoveListItem Component
 *
 * Reusable move list item for displaying moves with optional thumbnail and remove action.
 * Used in create/edit flows for series and tags.
 *
 * Features:
 * - Optional thumbnail with fallback emoji
 * - Move name and description display
 * - Remove button with aria-label
 * - Hover effect on border
 * - Consistent styling across pages
 */

import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface MoveListItemProps {
  /**
   * Unique identifier for the move
   */
  id: string

  /**
   * Display name of the move
   */
  name: string

  /**
   * Description of the move
   */
  description?: string

  /**
   * Optional thumbnail URL
   */
  thumbnailUrl?: string | null

  /**
   * Whether to show thumbnail section
   * @default false
   */
  showThumbnail?: boolean

  /**
   * Callback when remove button is clicked
   */
  onRemove: (moveId: string) => void

  /**
   * Optional additional CSS classes
   */
  className?: string
}

/**
 * Displays a single move in a list with optional thumbnail and remove button
 */
export function MoveListItem({
  id,
  name,
  description,
  thumbnailUrl,
  showThumbnail = false,
  onRemove,
  className,
}: MoveListItemProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border border-[#e5e7eb] bg-white p-3 transition-colors hover:border-[#0033A0]/30',
        className
      )}
    >
      {/* Thumbnail (optional) */}
      {showThumbnail && (
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#e5e7eb] bg-[#F9F9F9]">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="text-2xl" aria-hidden="true">
              🥋
            </span>
          )}
        </div>
      )}

      {/* Move Info */}
      <div className="min-w-0 flex-1">
        <h3 className="mb-1 line-clamp-1 font-medium text-black">{name}</h3>
        {description && (
          <p className="line-clamp-2 text-sm text-[#666666]">{description}</p>
        )}
      </div>

      {/* Remove Button */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove(id)}
        className="text-[#D62828] hover:bg-[#D62828]/10"
        aria-label={`Remove ${name}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}
